import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Users, Loader2, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { StarRating } from "./StarRating";
import type { VendorContact } from "./types";

interface Props {
  contacts: VendorContact[];
  eventCompleted?: boolean;
  onUpdate: (vendorId: string, updates: Partial<VendorContact>) => void;
}

const EXPECTED_CATEGORIES = [
  "Wedding Planner / Event Coordinator",
  "Venue POC",
  "DJ / AV / Sound",
  "Decorator / Florist",
  "Caterer",
  "Photographer / Videographer",
];

export function VendorContactsCard({ contacts, eventCompleted, onUpdate }: Props) {
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const saveReview = async (v: VendorContact) => {
    const rating = ratings[v.id] || 0;
    if (rating === 0) { toast.error("Please select a rating"); return; }
    setSaving(v.id);
    try {
      const update = { vendor_rating: rating, vendor_review_notes: notes[v.id] || null };
      onUpdate(v.id, update);
      const { error } = await supabase.from("vendor_contacts").update(update).eq("id", v.id);
      if (error) throw error;
      toast.success(`Reviewed ${v.name}`);
      setReviewingId(null);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="h-4 w-4" /> Vendor Contacts ({contacts.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {contacts.length === 0 ? (
          <div className="space-y-2">
            <div className="flex items-start gap-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded-md">
              <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-[10px] text-amber-700">
                <p className="font-medium mb-1">No vendor contacts added yet</p>
                <p className="text-muted-foreground">Expected contacts for this event:</p>
                <ul className="list-disc list-inside mt-1 space-y-0.5 text-muted-foreground">
                  {EXPECTED_CATEGORIES.map(cat => (
                    <li key={cat}>{cat}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : contacts.map(v => (
          <div key={v.id} className="p-2 border rounded-md space-y-1">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{v.name}</p>
              <Badge variant="outline" className="text-[9px] h-4">{v.category}</Badge>
            </div>
            {v.company && <p className="text-[10px] text-muted-foreground">{v.company}</p>}
            <div className="flex items-center gap-3 text-xs">
              {v.phone && <a href={`tel:${v.phone}`} className="text-primary underline">{v.phone}</a>}
              {v.email && <a href={`mailto:${v.email}`} className="text-primary underline">{v.email}</a>}
            </div>

            {/* Review section */}
            {v.vendor_rating ? (
              <div className="flex items-center gap-2 pt-1">
                <StarRating value={v.vendor_rating} readonly size="sm" />
                {v.vendor_review_notes && <span className="text-[9px] text-muted-foreground truncate">{v.vendor_review_notes}</span>}
              </div>
            ) : (
              reviewingId === v.id ? (
                <div className="space-y-1.5 pt-1">
                  <StarRating value={ratings[v.id] || 0} onChange={val => setRatings(p => ({ ...p, [v.id]: val }))} />
                  <Textarea
                    placeholder="Review notes (required, 20+ chars)..."
                    rows={2}
                    value={notes[v.id] || ""}
                    onChange={e => setNotes(p => ({ ...p, [v.id]: e.target.value }))}
                    className="text-xs"
                  />
                  <div className="flex items-center justify-between">
                    <span className={`text-[9px] ${(notes[v.id] || "").length < 20 ? "text-amber-600" : "text-green-600"}`}>
                      {(notes[v.id] || "").length}/20 chars
                    </span>
                    <Button size="sm" className="text-xs h-6" disabled={saving === v.id || (notes[v.id] || "").length < 20} onClick={() => saveReview(v)}>
                      {saving === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save Review"}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" variant="outline" className="text-[10px] h-5 mt-1" onClick={() => setReviewingId(v.id)}>
                  Rate Vendor
                </Button>
              )
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
